import React, { useState } from 'react';
import {
  Typography,
  Box,
  Avatar,
  Divider,
  Grid,
  Paper,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { makeStyles, createStyles } from '@mui/styles';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import Chip from '@mui/material/Chip';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { Mentor, WeeklySchedule } from 'types';
import { ControlledField, ControlledTimeSlotSelect } from 'components';
import { Button, NexForm } from 'components';
import { User } from 'firebase/auth';
import { createAppointRequest } from 'apis/door';
import { formatWeeklySchedule, generateAppointmentSlots, AppointmentSlot } from 'helper/time';

// Extend dayjs with timezone support
dayjs.extend(utc);
dayjs.extend(timezone);
const useStyles = makeStyles(() =>
createStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    height: '80vh',
    padding: '16px',
    width: '600px',
    '@media (max-width: 600px)': {width: '350px',},
    backgroundColor: '#FFFFFF',
    borderRadius: '12px',
    boxshadow: '4px 4px 12px 0px #00000040',
    border: '1px solid #C6CBD3',
    position: 'absolute',
    top: '50%',
    left: '50%',
    maxHeight: '80vh',
    transform: 'translate(-50%, -50%)',
    overflow: 'auto'
  },
  InfoBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  FormBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    padding: '16px 32px',
  },
  FontBoldL:{
    minwidth: '200px',
    height: '24px',
    fontFamily: 'Noto Sans TC',
    fontSize: '24px',
    fontWeight: '700',
    lineHeight: '30px',
    textAlign: 'left',
  },
  FontMed:{
    fontFamily: 'Noto Sans TC',
    fontSize: '16px',
    fontWeight: '500',
    lineHeight: '22.4px',
    textAlign: 'left',
    whiteSpace: 'nowrap',
  },
  ChipBox:{
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gridColumnGap: '16px',
    gridRowGap: '8px',
  },
  CustomChip:{
    width:'auto',
    maxwidth:'133px',
    height:'28px',
    borderRadius:'8px',
    padding:'12px',
    backgroundColor:'#C6F5D5',
    '& .MuiChip-label':{
      fontFamily:'Noto Sans TC',
      fontSize:'14px',
      fontWeight:400,
      lineHeight:'19.6px',
      color:'#000000DE',
      whiteSpace:'nowrap',
      padding:'0px',
      textAlign:'center',
    }
  },
  FontReg:{
    fontFamily: 'Noto Sans TC',
    fontSize: '16px',
    fontWeight: '400',
    lineHeight: '22.4px',
    textAlign: 'left',
  },
  successMessage: {
    marginTop: '16px',
    padding: '16px',
    backgroundColor: '#E8F5E8',
    borderRadius: '8px',
    border: '1px solid #4CAF50',
  },
  successText: {
    fontFamily: 'Noto Sans TC',
    fontSize: '16px',
    fontWeight: '500',
    lineHeight: '22.4px',
    color: '#2E7D32',
    textAlign: 'center',
  },
  })
);

interface AppointmentRequestModalProps {
  mentor: Mentor;
  user?: User;
  closeModal: () => void;
}
export const AppointmentRequestModal :React.FC<AppointmentRequestModalProps>  = ({mentor, user, closeModal}) => {
  const classes = useStyles();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const AvatorName = mentor.name.split(' ').map((name) => name[0]).join('');
  
  // Handle both old string format and new WeeklySchedule format
  const getFormattedAvailableTime = (availableTime: string | WeeklySchedule): string => {
    if (typeof availableTime === 'string') {
      return availableTime;
    } else {
      return formatWeeklySchedule(availableTime);
    }
  };
  
  const MENTOR_AVAILABLE_TIME = `${mentor.name} 提議: ${getFormattedAvailableTime(mentor.availableTime)}`;
  
  // Generate specific 30-minute appointment slots for the next two weeks
  // This shows students exactly when they can book appointments with dates
  const getAppointmentSlots = (): AppointmentSlot[] => {
    if (typeof mentor.availableTime === 'string') {
      return []; // Return empty array for old string format
    } else {
      // Use mentor's timezone if available, fallback to system timezone
      const mentorTimezone = mentor.timezone || dayjs.tz.guess();
      // Auto-detect student's timezone for conversion
      const studentTimezone = dayjs.tz.guess();
      
      return generateAppointmentSlots(mentor.availableTime, mentorTimezone, studentTimezone);
    }
  };

  const appointmentSlots = getAppointmentSlots();
  
  // Get timezone display info
  const getTimezoneDisplayInfo = () => {
    const mentorTz = mentor.timezone || dayjs.tz.guess();
    const studentTz = dayjs.tz.guess(); // Auto-detect student's timezone
    const mentorOffset = dayjs().tz(mentorTz).format('Z');
    const studentOffset = dayjs().tz(studentTz).format('Z');
    
    return {
      mentorTimezone: `導師時區: UTC${mentorOffset}`,
      studentTimezone: studentTz !== mentorTz ? `您的時區: UTC${studentOffset}` : null
    };
  };
  
  const timezoneInfo = getTimezoneDisplayInfo();
  
  const {
    control,
    handleSubmit,
    formState: { isSubmitting, errors },
  } = useForm({
    defaultValues: {
      email: '',
      name: '',
      message: '',
      availableTime: '',
    },
  });
  const onSubmit = async (data: any) => {
    const appointRequest = {
      receiverUid: mentor.uid,
      receiverName: mentor.name,
      receiverEmail: mentor.email,
      senderUid: user? user.uid : 'External User',
      senderName: data.name,
      senderEmail: data.email,
      message: data.message + "\n Available Time: " + data.availableTime,
    }
    createAppointRequest(appointRequest);
    setIsSubmitted(true);
  }
  const renderTags = (tags: string[] | undefined, classes: any) => {
    if (!tags || tags.length === 0) return null;
    return tags.map((label, index) => (
      <Chip key={index} className={classes.CustomChip} label={label} />
    ));
  };
  return (
      <Box className={classes.root}>
        <Box className={classes.InfoBox}>
          <Box sx= {{display:"flex", flexDirection:'row',gap:'24px'}}>
            <Avatar sx={{ width: 120, height: 120, fontSize: 36}}>{AvatorName}</Avatar>
            <Box sx= {{display:"flex", flexDirection:'column',gap:'8px'}}>
              <Typography className={classes.FontBoldL}>{mentor.name}</Typography>
              {mentor.title ?
                <Box sx={{width: '156px', height: '24px'}}>
                  <Typography className={classes.FontMed}>{mentor.title}</Typography>
                </Box> : null
              }
              {mentor.countryCode ?
                <Box sx={{width:'60px', height:'24px', gap:'4px', display:'flex', flexDirection:'row'}}>
                  <LocationOnIcon color="disabled" sx={{width: '24px', height: '24px'}}/>
                  <Typography className={classes.FontMed}>{mentor.countryCode}</Typography>
                </Box>: null
              }
            </Box>
          </Box>
          <Box className={classes.ChipBox}>
            {renderTags(mentor.tags, classes)}
          </Box>
          
          {/* Display mentor's available time slots for next two weeks */}
          <Box sx={{ 
            padding: "12px 16px", 
            backgroundColor: "#f5f5f5", 
            borderRadius: "8px",
            border: "1px solid #e0e0e0"
          }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, marginBottom: "8px" }}>
              📅 未來兩週可預約的時間
            </Typography>
            {timezoneInfo.studentTimezone && (
              <Typography variant="body2" sx={{ color: "#666", fontSize: "11px", marginBottom: "8px" }}>
                {timezoneInfo.mentorTimezone} | {timezoneInfo.studentTimezone}
              </Typography>
            )}
            
            {appointmentSlots.length > 0 ? (
              <Box sx={{ maxHeight: "200px", overflowY: "auto" }}>
                <Grid container spacing={1}>
                  {appointmentSlots.map((slot, index) => (
                    <Grid item xs={6} key={index}>
                      <Paper 
                        elevation={0} 
                        sx={{ 
                          padding: "8px 12px", 
                          backgroundColor: "#fff", 
                          border: "1px solid #e0e0e0",
                          borderRadius: "6px",
                          marginBottom: "4px"
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "12px" }}>
                          {slot.displayDate}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#666", fontSize: "11px" }}>
                          導師時間: {slot.mentorDisplayTime}
                        </Typography>
                        {slot.studentDisplayTime && (
                          <Typography variant="body2" sx={{ color: "#2196f3", fontSize: "11px", fontWeight: 500 }}>
                            您的時間: {slot.studentDisplayTime}
                          </Typography>
                        )}
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            ) : (
              <Typography variant="body2" sx={{ color: "#666" }}>
                {typeof mentor.availableTime === 'string' 
                  ? getFormattedAvailableTime(mentor.availableTime)
                  : "No specific time slots available"
                }
              </Typography>
            )}
          </Box>
          
          <Box sx ={{padding:"16px 0px"}}>
            <Typography className={classes.FontReg}>{mentor.profileBio}</Typography>
          </Box>
        </Box>
        <Divider/>
        {isSubmitted ? (
          <Box className={classes.FormBox}>
            <Box className={classes.successMessage}>
              <Typography className={classes.successText}>
                ✅ 預約請求已成功送出！
              </Typography>
              <Typography className={classes.successText} sx={{ marginTop: '8px', fontSize: '14px' }}>
                您的預約請求已發送給 {mentor.name}，請等待對方回覆確認預約時間。
              </Typography>
              {mentor.bookingLink && (
                <Box sx={{ marginTop: '12px', textAlign: 'center' }}>
                  <Typography className={classes.successText} sx={{ fontSize: '14px', marginBottom: '8px' }}>
                    您也可以直接使用以下預約連結：
                  </Typography>
                  <a
                    href={mentor.bookingLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ 
                      color: "#1976d2", 
                      wordBreak: "break-all",
                      textDecoration: "underline",
                      fontSize: "14px"
                    }}
                  >
                    {mentor.bookingLink}
                  </a>
                </Box>
              )}
            </Box>
            <Button onClick={closeModal} sx={{ marginTop: '16px' }}>
              關閉
            </Button>
          </Box>
        ) : (
          <NexForm gap={1} handleSubmit={handleSubmit(onSubmit)}>
            <Box className={classes.FormBox} >
              <Typography className={classes.FontBoldL}>向{mentor.name}預約</Typography>
              <Box display="grid" gap={2.5}>
                <ControlledField
                  label="Email"
                  name="email"
                  control={control}
                  required
                  error={errors.email}
                />
                <ControlledField
                  label="Name"
                  name="name"
                  control={control}
                  required
                  error={errors.name}
                />
                <ControlledField
                  label="Message"
                  name="message"
                  control={control}
                  required
                  multiline
                  rows={9}
                  error={errors.message}
                />
                {/* Available Time Selection - Dropdown for new format, text input for old format */}
                {appointmentSlots.length > 0 ? (
                  <ControlledTimeSlotSelect
                    label="可預約的時間"
                    name="availableTime"
                    control={control}
                    required
                    error={errors.availableTime}
                    appointmentSlots={appointmentSlots}
                    helperText="選擇您希望預約的30分鐘時段"
                  />
                ) : (
                  <ControlledField
                    label="可預約的時間"
                    name="availableTime"
                    control={control}
                    required
                    error={errors.availableTime}
                    helperText={MENTOR_AVAILABLE_TIME}
                    placeholder="ex: Monday 15:00-19:00, Friday 13:00-19:00"
                  />
                )}
                <Button type="submit" disabled={isSubmitting} >
                  確認預約
                </Button>
              </Box>
            </Box>
          </NexForm>
        )}
    </Box>
  );
};

export default AppointmentRequestModal;